import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { Input } from './Input'
import styles from './SearchInput.module.css'

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** Milliseconds of quiet before the change is published. */
  delay?: number
  className?: string
  label?: string
}

/**
 * Search box that keeps typing responsive while the request waits.
 *
 * The field is uncontrolled between keystrokes and only publishes after a pause,
 * so a list of thousands of trips is not re-queried on every character.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  delay = 300,
  className,
  label = 'Search',
}: SearchInputProps) {
  const [draft, setDraft] = useState(value)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Adopt changes made elsewhere (a "clear filters" button, a restored URL).
  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (draft === value) return

    const timer = setTimeout(() => onChangeRef.current(draft), delay)
    return () => clearTimeout(timer)
  }, [draft, delay, value])

  return (
    <Input
      type="search"
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className={className}
      iconLeft={<Search aria-hidden="true" />}
      addonRight={
        draft ? (
          <button
            type="button"
            className={styles.clear}
            onClick={() => {
              setDraft('')
              onChangeRef.current('')
            }}
            aria-label="Clear search"
          >
            <X aria-hidden="true" />
          </button>
        ) : null
      }
    />
  )
}
